import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import {
  Award,
  BrainCircuit,
  Code2,
  ExternalLink,
  Layers,
  Rocket,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { translations } from '../translations/translations';
import SectionWrapper, { SectionHeader } from '../components/SectionWrapper';
import { fadeUp, hoverElevation } from '../motion/presets';
import '../styles/Certificates.css';

const featuredTitles = [
  'Neural Networks and Deep Learning',
  'Improving Deep Neural Networks',
  'Supervised Machine Learning',
];

const categoryIcons = {
  ml: BrainCircuit,
  deepLearning: Sparkles,
  backend: Code2,
  mobile: Smartphone,
  foundations: Layers,
  product: Rocket,
};

function getCertificateCategory(title) {
  const normalizedTitle = title.toLowerCase();

  if (
    normalizedTitle.includes('neural') ||
    normalizedTitle.includes('convolutional') ||
    normalizedTitle.includes('deep learning')
  ) {
    return 'deepLearning';
  }

  if (
    normalizedTitle.includes('machine learning') ||
    normalizedTitle.includes('learning algorithms') ||
    normalizedTitle.includes('ai for everyone') ||
    normalizedTitle.includes('structuring machine learning')
  ) {
    return 'ml';
  }

  if (normalizedTitle.includes('spring boot')) return 'backend';
  if (normalizedTitle.includes('flutter')) return 'mobile';
  if (normalizedTitle.includes('data structures')) return 'foundations';
  if (normalizedTitle.includes('git') || normalizedTitle.includes('girişimcilik')) return 'product';

  return 'foundations';
}

function getProvider(title) {
  const normalizedTitle = title.toLowerCase();

  if (
    normalizedTitle.includes('machine learning') ||
    normalizedTitle.includes('learning algorithms') ||
    normalizedTitle.includes('neural') ||
    normalizedTitle.includes('convolutional') ||
    normalizedTitle.includes('ai for everyone') ||
    normalizedTitle.includes('structuring machine learning')
  ) {
    return 'DeepLearning.AI / Coursera';
  }

  if (normalizedTitle.includes('spring boot')) return 'Commencis';
  if (normalizedTitle.includes('flutter')) return 'BTK Akademi';
  if (normalizedTitle.includes('git')) return 'BTK Akademi';
  if (normalizedTitle.includes('girişimcilik')) return 'Bilkent / Entrepreneurship';

  return 'Coursera';
}

function enrichCertificate(cert) {
  return {
    ...cert,
    category: getCertificateCategory(cert.title),
    provider: getProvider(cert.title),
    featured: featuredTitles.some((title) => cert.title.includes(title)),
  };
}

export default function Certificates() {
  const [certificates, setCertificates] = useState([]);
  const [selectedFeaturedIndex, setSelectedFeaturedIndex] = useState(0);
  const { language } = useAppContext();
  const t = translations[language];

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/certificates/certificates.json`)
      .then((res) => res.json())
      .then((data) => setCertificates(data))
      .catch(() => setCertificates([]));
  }, []);

  if (certificates.length === 0) return null;

  const enrichedCertificates = certificates.map(enrichCertificate);
  const featuredCertificates = enrichedCertificates.filter((cert) => cert.featured).slice(0, 3);
  const selectedFeaturedCertificate = featuredCertificates[selectedFeaturedIndex] || featuredCertificates[0];
  const libraryCertificates = enrichedCertificates.filter((cert) => !cert.featured);
  const aiCertificateCount = enrichedCertificates.filter((cert) => (
    cert.category === 'ml' || cert.category === 'deepLearning'
  )).length;
  const categoryOrder = ['ml', 'deepLearning', 'backend', 'mobile', 'foundations', 'product'];
  const skillTrackCount = categoryOrder.filter((category) => (
    enrichedCertificates.some((cert) => cert.category === category)
  )).length;

  return (
    <SectionWrapper id="certificates" className="certificates">
      <SectionHeader
        label={t.certificates.label}
        title={t.certificates.title}
        subtitle={t.certificates.subtitle}
      />

      <motion.div className="credentials-showcase" variants={fadeUp}>
        <div className="credentials-showcase__hero">
          <div
            className={`credential-orbit credential-orbit--step-${selectedFeaturedIndex}`}
            aria-hidden="true"
          >
            <span className="credential-orbit__core" />
            <span className="credential-orbit__node credential-orbit__node--one" />
            <span className="credential-orbit__node credential-orbit__node--two" />
            <span className="credential-orbit__node credential-orbit__node--three" />
            <span className="credential-orbit__node credential-orbit__node--four" />
            <span className="credential-orbit__node credential-orbit__node--five" />
            <span className="credential-orbit__link credential-orbit__link--one" />
            <span className="credential-orbit__link credential-orbit__link--two" />
          </div>

          <div className="credentials-showcase__copy">
            <span className="credentials-showcase__eyebrow">{t.certificates.featured}</span>
            <h3>{t.certificates.trackTitle}</h3>
            <p>{t.certificates.trackCopy}</p>
          </div>

          <div className="credentials-showcase__stats">
            <Metric value={enrichedCertificates.length} label={t.certificates.stats.total} />
            <Metric value={aiCertificateCount} label={t.certificates.stats.ai} />
            <Metric value={skillTrackCount} label={t.certificates.stats.tracks} />
          </div>

          <div
            className={`featured-credential-chooser featured-credential-chooser--step-${selectedFeaturedIndex}`}
            role="tablist"
            aria-label={t.certificates.featured}
          >
            {featuredCertificates.map((cert, index) => {
              const shortTitle = cert.title
                .replace(': Regression and Classification', '')
                .replace(': Hyperparameter Tuning, Regularization and Optimization', '');

              return (
                <button
                  key={cert.pdfLink}
                  type="button"
                  className={`featured-credential-choice${selectedFeaturedIndex === index ? ' is-active' : ''}`}
                  onClick={() => setSelectedFeaturedIndex(index)}
                  role="tab"
                  aria-selected={selectedFeaturedIndex === index}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{shortTitle}</strong>
                </button>
              );
            })}
          </div>

          <div className="featured-credential-stage">
            <AnimatePresence mode="wait">
              {selectedFeaturedCertificate && (
                <FeaturedCredential
                  key={selectedFeaturedCertificate.pdfLink}
                  cert={selectedFeaturedCertificate}
                  index={selectedFeaturedIndex}
                  label={t.certificates.viewCertificate}
                  categoryLabel={t.certificates.categories[selectedFeaturedCertificate.category]}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="credentials-showcase__library">
          <div className="credentials-library__header">
            <h3>{t.certificates.library}</h3>
            <span>{libraryCertificates.length} {t.certificates.credentialLabel}</span>
          </div>

          <div className="credentials-library__list">
            {libraryCertificates.map((cert) => (
              <CredentialRow
                key={cert.pdfLink}
                cert={cert}
                label={t.certificates.viewCertificate}
                categoryLabel={t.certificates.categories[cert.category]}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </SectionWrapper>
  );
}

function Metric({ value, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;

    let frameId;
    const duration = 900;
    const start = performance.now();

    const tick = (time) => {
      const progress = Math.min((time - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * eased));

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [inView, value]);

  return (
    <div ref={ref} className="credential-metric">
      <span className="credential-metric__value">{displayValue}</span>
      <span className="credential-metric__label">{label}</span>
    </div>
  );
}

function FeaturedCredential({ cert, index, label, categoryLabel }) {
  const Icon = categoryIcons[cert.category] || Award;

  return (
    <motion.a
      href={`${process.env.PUBLIC_URL}${cert.pdfLink}`}
      target="_blank"
      rel="noopener noreferrer"
      className="featured-credential"
      style={{ '--stack-index': index }}
      initial={{ opacity: 0, y: 12, scale: 0.985, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      exit={{ opacity: 0, y: -8, scale: 0.985, filter: 'blur(8px)' }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      {...hoverElevation}
    >
      <div className="featured-credential__icon">
        <Icon size={20} />
      </div>
      <div className="featured-credential__content">
        <span>{categoryLabel}</span>
        <h4>{cert.title}</h4>
        <p>{cert.provider}</p>
      </div>
      <span className="featured-credential__link">
        {label}
        <ExternalLink size={12} />
      </span>
      <span className="featured-credential__sweep" aria-hidden="true" />
    </motion.a>
  );
}

function CredentialRow({ cert, label, categoryLabel }) {
  const Icon = categoryIcons[cert.category] || Award;

  return (
    <motion.a
      href={`${process.env.PUBLIC_URL}${cert.pdfLink}`}
      target="_blank"
      rel="noopener noreferrer"
      className="credential-row"
      variants={fadeUp}
      whileHover={{ x: 4 }}
      transition={{ type: 'spring', stiffness: 220, damping: 24 }}
    >
      <span className="credential-row__icon">
        <Icon size={15} />
      </span>
      <span className="credential-row__body">
        <span className="credential-row__title">{cert.title}</span>
        <span className="credential-row__meta">{categoryLabel} / {cert.provider}</span>
      </span>
      <span className="credential-row__link">
          {label}
          <ExternalLink size={12} />
      </span>
    </motion.a>
  );
}
